import React from 'react';

import { defineMessages, type MessageDescriptor, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Inline } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

export type ModalHeaderProps = {
	leftElement?: React.ReactNode;
	heading?: MessageDescriptor;
	onClose: () => void;
	closeButtonRef?: React.Ref<HTMLButtonElement>;
};

const i18n = defineMessages({
	defaultCloseButtonLabel: {
		id: 'ai-audio.modal-header.close-button-label',
		defaultMessage: 'Close audio player',
		description: 'Default label for the close button in modal headers',
	},
});

const styles = cssMap({
	headerSection: {
		paddingBlock: token('space.050'),
	},
	headerContent: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
	},
	leftSection: {
		display: 'flex',
		alignItems: 'center',
	},
	heading: {
		paddingLeft: token('space.100'),
	},
});

/**
 * A clean, reusable modal header component that provides consistent styling.
 * Supports optional left element (back button, overflow menu), heading, and close button.
 */
export const ModalHeader = ({
	leftElement,
	heading,
	onClose,
	closeButtonRef,
}: ModalHeaderProps): React.JSX.Element => {
	const { formatMessage } = useIntl();

	const getDisplayText = (text: string | MessageDescriptor): string => {
		return typeof text === 'string' ? text : formatMessage(text);
	};

	return (
		<Box xcss={styles.headerSection}>
			<Inline
				space="space.100"
				alignBlock="center"
				spread="space-between"
				xcss={styles.headerContent}
			>
				<Inline alignBlock="center">
					<Box xcss={styles.leftSection}>{leftElement}</Box>
					{heading && (
						<Box xcss={styles.heading}>
							<Heading size="xsmall" as="h2">
								{getDisplayText(heading)}
							</Heading>
						</Box>
					)}
				</Inline>

				<IconButton
					ref={closeButtonRef}
					appearance="subtle"
					spacing="compact"
					icon={CrossIcon}
					label={getDisplayText(i18n.defaultCloseButtonLabel)}
					onClick={onClose}
					testId={'modal-header-close-button'}
				/>
			</Inline>
		</Box>
	);
};
